import { useCallback, useEffect, useState } from 'react'
import { Download, KeyRound, Plus, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

async function fetchSecretKeys(projectId) {
  const { data, error } = await supabase
    .from('hub_project_secrets')
    .select('id, project_id, key, created_at, updated_at')
    .eq('project_id', projectId)
    .order('key')
  if (error) throw error
  return data || []
}

export default function ProjectSecretsPanel({ projectId, slug, className, onChanged }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [keyInput, setKeyInput] = useState('')
  const [valueInput, setValueInput] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError('')
    try {
      const data = await fetchSecretKeys(projectId)
      setRows(data)
      onChanged?.()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [projectId, onChanged])

  useEffect(() => {
    reload()
  }, [reload])

  const addSecret = async () => {
    const key = keyInput.trim().toUpperCase().replace(/\s+/g, '_')
    if (!projectId || !key || !valueInput) return
    setBusy(true)
    setError('')
    try {
      const { error: upErr } = await supabase.from('hub_project_secrets').upsert(
        {
          project_id: projectId,
          key,
          value: valueInput,
        },
        { onConflict: 'project_id,key' }
      )
      if (upErr) throw upErr
      setKeyInput('')
      setValueInput('')
      await reload()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const removeSecret = async (id) => {
    if (!projectId || !id) return
    setBusy(true)
    setError('')
    try {
      const { error: delErr } = await supabase.from('hub_project_secrets').delete().eq('id', id)
      if (delErr) throw delErr
      await reload()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  const downloadEnv = async () => {
    if (!projectId) return
    setBusy(true)
    setError('')
    try {
      const { data, error: rpcErr } = await supabase.rpc('hub_export_project_env', {
        p_project_id: projectId,
      })
      if (rpcErr) throw rpcErr
      const text = typeof data === 'string' ? data : ''
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${slug || 'project'}.env`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('rounded-2xl border border-border/80 bg-muted/5 p-5', className)}>
      <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
        <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden />
        Secrets
      </div>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
        Values are stored but never shown again here. Use download to retrieve a merged <code className="font-mono">.env</code>{' '}
        file on a trusted device.
      </p>

      {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}

      <div className="mt-4 space-y-2">
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading keys…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No secrets yet.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/80 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="font-mono text-xs font-medium text-foreground">{r.key}</p>
                  <p className="text-[11px] text-muted-foreground">••••••••••••</p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => removeSecret(r.id)}
                  className="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                  aria-label={`Remove ${r.key}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground" htmlFor="sec-key">
            Variable
          </label>
          <input
            id="sec-key"
            className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="API_KEY"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-muted-foreground" htmlFor="sec-val">
            Value
          </label>
          <input
            id="sec-val"
            type="password"
            className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
            value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            placeholder="Paste once"
            autoComplete="new-password"
          />
        </div>
        <button
          type="button"
          disabled={busy || !keyInput.trim() || !valueInput}
          onClick={addSecret}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 text-xs font-medium hover:bg-muted disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      <button
        type="button"
        disabled={busy || rows.length === 0}
        onClick={downloadEnv}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
      >
        <Download className="h-4 w-4" />
        Download merged .env
      </button>
    </div>
  )
}
